const routePath =
  "M 120 500 C 230 495 200 360 330 330 C 460 300 480 210 690 130";

const verticalStreets = [40, 130, 220, 310, 490, 580, 670, 760];
const horizontalStreets = [60, 150, 240, 420, 510];

/**
 * Stylized placeholder city map with the active route, pins and a car
 * marker that moves along the route (SMIL animation, no JS needed).
 */
export function TrackingMap() {
  return (
    <svg
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      role="img"
      aria-label="Mapa ilustrado del recorrido en Zapopan y Guadalajara, Jalisco"
    >
      {/* Ground */}
      <rect width="800" height="600" style={{ fill: "var(--muted)" }} />

      {/* City blocks */}
      <g
        style={{ fill: "var(--card)", stroke: "var(--border)" }}
        strokeWidth="1.5"
      >
        <rect x="150" y="170" width="52" height="52" rx="12" />
        <rect x="230" y="430" width="58" height="58" rx="12" />
        <rect x="512" y="62" width="48" height="68" rx="12" />
        <rect x="602" y="252" width="48" height="56" rx="12" />
        <rect x="62" y="252" width="48" height="58" rx="12" />
      </g>

      {/* Plaza + lake */}
      <rect
        x="422"
        y="172"
        width="56"
        height="56"
        rx="14"
        style={{ fill: "var(--gold-soft)", stroke: "var(--accent)" }}
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />
      <ellipse cx="645" cy="472" rx="72" ry="36" style={{ fill: "var(--secondary)" }} />

      {/* Streets */}
      <g style={{ stroke: "var(--border)" }} strokeWidth="6" strokeLinecap="round">
        {verticalStreets.map((x) => (
          <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="600" />
        ))}
        {horizontalStreets.map((y) => (
          <line key={`h-${y}`} x1="0" y1={y} x2="800" y2={y} />
        ))}
      </g>

      {/* Avenues */}
      <g style={{ stroke: "var(--secondary)" }} strokeWidth="16" strokeLinecap="round">
        <line x1="400" y1="0" x2="400" y2="600" />
        <line x1="0" y1="330" x2="800" y2="330" />
      </g>

      {/* Route: soft glow + animated dashed line */}
      <path
        d={routePath}
        fill="none"
        style={{ stroke: "var(--accent)" }}
        strokeWidth="16"
        strokeLinecap="round"
        opacity="0.25"
      />
      <path
        d={routePath}
        fill="none"
        style={{ stroke: "var(--accent)" }}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray="14 12"
      >
        <animate
          attributeName="stroke-dashoffset"
          from="26"
          to="0"
          dur="1.1s"
          repeatCount="indefinite"
        />
      </path>

      {/* Origin pin */}
      <g transform="translate(120 500)">
        <circle r="16" style={{ fill: "var(--primary)" }} opacity="0.25">
          <animate attributeName="r" values="12;26;12" dur="2.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0.05;0.3" dur="2.6s" repeatCount="indefinite" />
        </circle>
        <circle
          r="11"
          style={{ fill: "var(--primary)", stroke: "var(--card)" }}
          strokeWidth="3.5"
        />
        <circle r="3.5" style={{ fill: "var(--card)" }} />
      </g>

      {/* Destination pin */}
      <g transform="translate(690 130)">
        <circle r="16" style={{ fill: "var(--accent)" }} opacity="0.35">
          <animate
            attributeName="r"
            values="12;28;12"
            dur="2.6s"
            begin="1.3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.4;0.06;0.4"
            dur="2.6s"
            begin="1.3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          r="11"
          style={{ fill: "var(--accent)", stroke: "var(--card)" }}
          strokeWidth="3.5"
        />
        <path
          d="M 0 -4.4 L 1.3 -1.4 L 4.4 -1.4 L 2 0.7 L 2.8 3.8 L 0 2 L -2.8 3.8 L -2 0.7 L -4.4 -1.4 L -1.3 -1.4 Z"
          style={{ fill: "var(--accent-foreground)" }}
        />
      </g>

      {/* Labels */}
      <g fontFamily="Quicksand, sans-serif" fontSize="12.5" fontWeight="700">
        <g transform="translate(120 546)">
          <rect
            x="-58"
            y="-13"
            width="116"
            height="26"
            rx="13"
            style={{ fill: "var(--card)", stroke: "var(--border)" }}
            strokeWidth="1.5"
          />
          <text textAnchor="middle" dy="4.5" style={{ fill: "var(--foreground)" }}>
            Casa · Zapopan
          </text>
        </g>
        <g transform="translate(690 92)">
          <rect
            x="-62"
            y="-13"
            width="124"
            height="26"
            rx="13"
            style={{ fill: "var(--card)", stroke: "var(--border)" }}
            strokeWidth="1.5"
          />
          <text textAnchor="middle" dy="4.5" style={{ fill: "var(--foreground)" }}>
            Vet · Guadalajara
          </text>
        </g>
      </g>

      {/* Car marker moving along the route */}
      <g>
        <animateMotion
          dur="16s"
          begin="-8s"
          repeatCount="indefinite"
          path={routePath}
          rotate="0"
        />
        <circle r="25" style={{ fill: "var(--accent)" }} opacity="0.2" />
        <circle
          r="16.5"
          style={{ fill: "var(--accent)", stroke: "var(--card)" }}
          strokeWidth="3.5"
        />
        <g>
          <rect x="-9" y="-1" width="18" height="6.5" rx="2.5" style={{ fill: "var(--card)" }} />
          <path d="M -6 -1 L -4 -5.2 L 2 -5.2 L 5 -1 Z" style={{ fill: "var(--card)" }} />
          <path d="M -4.2 -1.6 L -3 -4 L 1.4 -4 L 3.4 -1.6 Z" style={{ fill: "var(--primary)" }} />
          <circle cx="-5" cy="5.8" r="2.1" style={{ fill: "var(--primary)" }} />
          <circle cx="5" cy="5.8" r="2.1" style={{ fill: "var(--primary)" }} />
        </g>
      </g>
    </svg>
  );
}
